import React, { Fragment, PureComponent } from 'react'

import {
  formatDate,
  durationRemaining,
} from '@apiEnabler/ob-common/lib/date-utils'
import {
  accessDescriptiveDate,
  getHistoryForCurrentUser,
} from '@apiEnabler/ob-common/lib/access-consents'
import getPermissions from '@apiEnabler/ob-common/lib/permissions'
import DateTimeWarning from '@apiEnabler/ob-common/lib/view/DateTimeWarning'

import AccessAccountCard from './AccessAccountCard'
import AccessPermissionGroup from './AccessPermissionGroup'
import ConsentOverviewHeader from './ConsentOverviewHeader'
import HistoryCard from './HistoryCard'
import NavBar, { NavItem } from './NavBar'

import style from './AccessAgreement.module.scss'

export default class AccessAgreement extends PureComponent {
  render() {
    const {
      access,
      tpp,
      onCancelAccess,
      handleShowContinueAccess,
      accountNumberFormat,
      isStaffMember,
    } = this.props
    const { label, date } = accessDescriptiveDate(access)
    const handleCancel = createCancelHandler(access, onCancelAccess)

    return (
      <section className="js-access-container">
        <header className={style.header}>
          <div className={style.content}>
            <NavBar
              items={[
                new NavItem({
                  label: 'Access',
                  link: '/access-agreements/tpps',
                }),
                new NavItem({
                  label: tpp.clientDisplayName,
                  link: `/access-agreements/tpps/${tpp.clientId}/agreements`,
                }),
              ]}
              padded
            />
            <ConsentOverviewHeader
              dateLabel={label}
              dateValue={date}
              description={
                <AccessDescriptiveMessage
                  access={access}
                  tpp={tpp}
                  onShowContinueAccess={handleShowContinueAccess}
                />
              }
              onCancel={handleCancel}
              cancelLabel="Stop sharing"
              consentStatus={access.status}
            />
          </div>
        </header>

        <nav className={style.navBar}>
          <div className={style.navBarContent}>
            <ul className={style.navList}>
              <li>
                <a
                  className={style.navLink}
                  href="#accounts-title"
                  onClick={createScrollTo('#accounts-title')}
                >
                  Accounts
                </a>
              </li>
              <li>
                <a
                  className={style.navLink}
                  href="#details-title"
                  onClick={createScrollTo('#details-title')}
                >
                  Details
                </a>
              </li>
              <li>
                <a
                  className={style.navLink}
                  href="#history-title"
                  onClick={createScrollTo('#history-title')}
                >
                  History
                </a>
              </li>
            </ul>
          </div>
        </nav>

        <section className={style.content}>
          <h2
            id="accounts-title"
            className={`${style.sectionHeading} js-account-title`}
          >
            Accounts
          </h2>

          <div className={`${style.card} js-access-accounts-card`}>
            {access.accounts.size > 0
              ? access.accounts
                  .sortBy(sortingAccountName)
                  .map(
                    (account, i) =>
                      account && (
                        <AccessAccountCard
                          key={i}
                          account={account}
                          index={i}
                          isStaffMember={isStaffMember}
                        />
                      )
                  )
                  .toArray()
              : 'There are no accounts associated with this agreement record.'}
          </div>

          <h2
            id="details-title"
            className={`${style.sectionHeading} js-details-title`}
          >
            Details
          </h2>

          <div className={style.detailsContainer}>
            {getPermissions(access.get('payload'), accountNumberFormat).map(
              (item, i) => (
                <AccessPermissionGroup key={i} index={i} item={item} />
              )
            )}
          </div>

          <h2
            id="history-title"
            className={`${style.sectionHeading} js-history-title`}
          >
            History
          </h2>

          <div className="zb-card">
            {getHistoryForCurrentUser(access, isStaffMember).map(
              (record, i) => (
                <HistoryCard
                  key={i}
                  className={`js-history-card-${i}`}
                  history={record}
                />
              )
            )}
          </div>

          <DateTimeWarning />
        </section>
      </section>
    )
  }
}

function createScrollTo(selector) {
  return function scrollTo(event) {
    const element = document.querySelector(selector)
    if (element) {
      window.scrollBy(0, element.getBoundingClientRect().top - 120)
    }

    event.preventDefault()
  }
}

function createCancelHandler(access, onCancelAccess) {
  if (onCancelAccess && access.get('accessGrouping') === 'ACTIVE') {
    return () => {
      onCancelAccess(access)
    }
  }
  return null
}

export function AccessDescriptiveMessage({
  access,
  tpp,
  onShowContinueAccess,
}) {
  const createdDate = (
    <span className={style.descriptionDate}>
      {formatDate(access.get('createdAt'), 'Do MMMM YYYY')}
    </span>
  )
  const clientName = (
    <span className={style.descriptionTpp}>{tpp.clientDisplayName}</span>
  )

  const accessRemainingTime =
    access.accessExpiresAt && durationRemaining(access.accessExpiresAt)

  switch (access.accessGrouping) {
    case 'DELETED':
      return (
        <p className={style.description}>
          The account information shown below is no longer shared with{' '}
          {clientName}, as {clientName} you or a member of staff has chosen
          sharing.
        </p>
      )
    case 'WITHDRAWN':
      return (
        <p className={style.description}>
          The account information shown below is no longer shared with{' '}
          {clientName}, as you or a member of staff has chosen to stop sharing.
        </p>
      )
    case 'BLOCKED':
      return (
        <p className={style.description}>
          This status is not displayed in the customer access store. This
          agreement request has been blocked. This state occurs where there
          could potentially be harmful to the Bank and customer and is therefore
          denied.
        </p>
      )
    case 'DECLINED':
      return (
        <p className={style.description}>
          This status is not displayed in the customer access store. This
          agreement request has been declined. The authorisation process has
          been cancelled.
        </p>
      )
    case 'EXPIRED':
      return (
        <p className={style.description}>
          The account information shown below is no longer shared with{' '}
          {clientName}, as the connection you made on {createdDate} has expired.
        </p>
      )
    case 'TPP_REVOKED':
      return (
        <p className={style.description}>
          The account information shown below is no longer shared with{' '}
          {clientName}, as they have been revoked. Please contact {clientName}{' '}
          for further information.
        </p>
      )
    case 'TPP_SUSPENDED':
    case 'TPP_CLIENT_SUSPENDED':
      return (
        <p className={style.description}>
          The account information shown below is no longer shared with{' '}
          {clientName}, as they have been suspended. Please contact {clientName}{' '}
          for further information.
        </p>
      )
    default:
      return (
        <Fragment>
          <p className={style.description}>
            On {createdDate}, you agreed to share with {clientName} information
            for {access.accounts.size} of your accounts.
            {accessRemainingTime &&
              ` Connection expires in ${accessRemainingTime}.`}
          </p>
          {access.expiresSoon && (
            <p className={style.secondDescription}>
              <a
                onClick={onShowContinueAccess}
                className="js-continue-access-link"
              >
                How can I continue to have access?
              </a>
            </p>
          )}
        </Fragment>
      )
  }
}

function sortingAccountName(account) {
  return account.nameUnavailable ? '9' : `1${account.name}`
}
